/**
 * Upgrade Banner Component
 * Displays a persistent banner encouraging free users to upgrade
 * and non-authenticated users to register
 */

import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, Zap, ArrowRight, UserPlus } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { ROUTES, SUBSCRIPTION_PRICES } from '../../utils/constants';

export const UpgradeBanner: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { getCurrentTier } = useSubscription();
  const location = useLocation();
  const [isDismissed, setIsDismissed] = useState(false);
  const [isDelayedVisible, setIsDelayedVisible] = useState(false);

  // Determine user state
  const isFreeTier = getCurrentTier() === 'free';
  const showBanner = !isAuthenticated || isFreeTier;

  // Check if we are on the homepage
  const isHomePage = location.pathname === ROUTES.home || location.pathname === '/';
  // Check for the specific condition: non-logged-in visitor on the homepage
  const isNonLoggedInHomePage = !isAuthenticated && isHomePage;

  useEffect(() => {
    if (isNonLoggedInHomePage) {
      // Condition 1: Non-logged-in on homepage -> 30-second delay
      const timer = setTimeout(() => {
        setIsDelayedVisible(true);
      }, 30000); // 30 seconds

      return () => clearTimeout(timer);
    } else {
      // Condition 2: All other cases (logged in, or not homepage) -> show immediately
      setIsDelayedVisible(true);
    }
  }, [isNonLoggedInHomePage]);

  // Don't render if premium user, if dismissed, or if delayed and not yet visible for the specific case
  if (!showBanner || isDismissed || (isNonLoggedInHomePage && !isDelayedVisible)) {
    return null;
  }

  // Banner content for non-authenticated users
  if (!isAuthenticated) {
    return (
      <div className="bg-gradient-to-r from-primary to-secondary text-white py-3 px-4 relative">
        <div className="container-custom flex items-center justify-between">
          <div className="flex items-center space-x-3 flex-1">
            <UserPlus className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm md:text-base font-medium">
              <span className="hidden sm:inline">Register for free and take your Skills Assessment to see how AI can help you today!</span>
              <span className="sm:hidden font-semibold">Register for free and take your Skills Assessment!</span>
            </p>
          </div>
          
          <div className="flex items-center space-x-2 ml-4">
            <Link
              to={ROUTES.register}
              className="bg-white text-primary px-4 py-2 rounded-lg text-sm font-semibold hover:bg-opacity-90 transition-all flex items-center space-x-1 whitespace-nowrap"
            >
              <span>Register Now</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            
            <button
              onClick={() => setIsDismissed(true)}
              className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
              aria-label="Dismiss banner"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Banner content for authenticated free tier users
  return (
    <div className="bg-gradient-to-r from-primary to-secondary text-white py-3 px-4 relative">
      <div className="container-custom flex items-center justify-between">
        <div className="flex items-center space-x-3 flex-1">
          <Zap className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm md:text-base font-medium">
            <span className="hidden sm:inline">Unlock your tailored learning plan and private community support for just {SUBSCRIPTION_PRICES.standard.display} per month. </span>
            <span className="font-semibold">Upgrade to a standard membership today!</span>
          </p>
        </div>
        
        <div className="flex items-center space-x-2 ml-4">
          <Link
            to={ROUTES.pricing}
            className="bg-white text-primary px-4 py-2 rounded-lg text-sm font-semibold hover:bg-opacity-90 transition-all flex items-center space-x-1 whitespace-nowrap"
          >
            <span>View Plans</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
          
          <button
            onClick={() => setIsDismissed(true)}
            className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
            aria-label="Dismiss banner"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};
